import {
  CircleStackIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  ServerStackIcon,
  Square3Stack3DIcon,
} from '@heroicons/react/24/outline';
import { ComponentType, SVGProps, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  TechnologyOverviewComposition,
  TechnologyOverviewHosting,
} from '../../services/technologyApi';
import {
  TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS,
  technologyOptionLabel,
} from './technologyFormOptions';

const FLOW_COLORS = ['#3b82f6', '#22c55e', '#f97316', '#8b5cf6', '#06b6d4', '#eab308'];

const COMPONENT_ICONS: ComponentType<SVGProps<SVGSVGElement>>[] = [
  Cog6ToothIcon,
  ComputerDesktopIcon,
  Square3Stack3DIcon,
  CircleStackIcon,
];

const ENVIRONMENT_DOT: Record<string, string> = {
  Production: 'bg-emerald-500',
  Homologation: 'bg-sky-500',
  Development: 'bg-amber-500',
  DisasterRecovery: 'bg-violet-500',
};

type FlowPath = {
  id: string;
  d: string;
  color: string;
};

type EnvironmentGroup = {
  environmentKind: string;
  servers: TechnologyOverviewHosting[];
};

type TechnologyInfrastructureFlowMapProps = {
  composition: TechnologyOverviewComposition;
  environmentKind: string;
  selectedApplicationId?: string | null;
  onSelectApplication?: (applicationId: string) => void;
};

function environmentOrder(kind: string): number {
  const index = TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS.findIndex((option) => option.value === kind);
  return index < 0 ? TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS.length : index;
}

function groupEnvironments(hostings: TechnologyOverviewHosting[]): EnvironmentGroup[] {
  const groups = new Map<string, EnvironmentGroup>();
  for (const hosting of hostings) {
    const current = groups.get(hosting.environmentKind);
    if (!current) {
      groups.set(hosting.environmentKind, {
        environmentKind: hosting.environmentKind,
        servers: [hosting],
      });
      continue;
    }
    current.servers.push(hosting);
  }

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      servers: [...group.servers].sort((left, right) =>
        left.infrastructureAssetName.localeCompare(right.infrastructureAssetName, 'pt-BR')
        || (left.address ?? '').localeCompare(right.address ?? '', 'pt-BR'),
      ),
    }))
    .sort(
      (left, right) =>
        environmentOrder(left.environmentKind) - environmentOrder(right.environmentKind),
    );
}

function environmentLabel(kind: string): string {
  return technologyOptionLabel(TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS, kind);
}

function curve(fromX: number, fromY: number, toX: number, toY: number): string {
  const bend = Math.max(24, (toX - fromX) / 2);
  return `M ${fromX} ${fromY} C ${fromX + bend} ${fromY}, ${toX - bend} ${toY}, ${toX} ${toY}`;
}

export function TechnologyInfrastructureFlowMap({
  composition,
  environmentKind,
  selectedApplicationId,
  onSelectApplication,
}: TechnologyInfrastructureFlowMapProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const nodesRef = useRef<Map<string, HTMLElement>>(new Map());
  const [paths, setPaths] = useState<FlowPath[]>([]);
  const [canvas, setCanvas] = useState({ width: 0, height: 0 });

  const applications = useMemo(
    () =>
      [...composition.applications].sort((left, right) =>
        left.name.localeCompare(right.name, 'pt-BR'),
      ),
    [composition.applications],
  );

  function bindNode(id: string) {
    return (element: HTMLElement | null) => {
      if (element) {
        nodesRef.current.set(id, element);
        return;
      }
      nodesRef.current.delete(id);
    };
  }

  useLayoutEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    const measure = () => {
      const rootRect = root.getBoundingClientRect();
      const anchor = (id: string, side: 'left' | 'right') => {
        const element = nodesRef.current.get(id);
        if (!element) return null;
        const rect = element.getBoundingClientRect();
        return {
          x: (side === 'right' ? rect.right : rect.left) - rootRect.left + root.scrollLeft,
          y: rect.top + rect.height / 2 - rootRect.top + root.scrollTop,
        };
      };

      const next: FlowPath[] = [];
      applications.forEach((application, index) => {
        const color = FLOW_COLORS[index % FLOW_COLORS.length];
        const fromProduct = anchor('product', 'right');
        const toApplication = anchor(`app:${application.id}`, 'left');
        if (fromProduct && toApplication) {
          next.push({
            id: `product-${application.id}`,
            color,
            d: curve(fromProduct.x, fromProduct.y, toApplication.x, toApplication.y),
          });
        }

        const hostings = environmentKind
          ? application.hostings.filter((hosting) => hosting.environmentKind === environmentKind)
          : application.hostings;
        for (const environment of groupEnvironments(hostings)) {
          const fromApplication = anchor(`app:${application.id}`, 'right');
          const toEnvironment = anchor(`env:${application.id}:${environment.environmentKind}`, 'left');
          if (fromApplication && toEnvironment) {
            next.push({
              id: `app-${application.id}-${environment.environmentKind}`,
              color,
              d: curve(fromApplication.x, fromApplication.y, toEnvironment.x, toEnvironment.y),
            });
          }
          for (const server of environment.servers) {
            const fromEnvironment = anchor(`env:${application.id}:${environment.environmentKind}`, 'right');
            const toServer = anchor(
              `server:${application.id}:${environment.environmentKind}:${server.id}`,
              'left',
            );
            if (fromEnvironment && toServer) {
              next.push({
                id: `server-${application.id}-${environment.environmentKind}-${server.id}`,
                color,
                d: curve(fromEnvironment.x, fromEnvironment.y, toServer.x, toServer.y),
              });
            }
            const fromServer = anchor(
              `server:${application.id}:${environment.environmentKind}:${server.id}`,
              'right',
            );
            const toAddress = anchor(
              `address:${application.id}:${environment.environmentKind}:${server.id}`,
              'left',
            );
            if (fromServer && toAddress) {
              next.push({
                id: `address-${application.id}-${environment.environmentKind}-${server.id}`,
                color,
                d: curve(fromServer.x, fromServer.y, toAddress.x, toAddress.y),
              });
            }
          }
        }
      });

      setPaths((current) => {
        if (
          current.length === next.length &&
          current.every((item, index) => item.d === next[index]?.d && item.color === next[index]?.color)
        ) {
          return current;
        }
        return next;
      });
      setCanvas((current) => {
        const width = root.scrollWidth;
        const height = root.scrollHeight;
        return current.width === width && current.height === height ? current : { width, height };
      });
    };

    measure();
    const frame = window.requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [applications, environmentKind, composition.salesProductId]);

  return (
    <div className="overflow-x-auto">
      <div className="mb-4 grid min-w-[76rem] grid-cols-[12rem_12rem_10rem_12rem_14rem] gap-x-16 text-sm font-medium text-secondary-500">
        <span>Aplicação</span>
        <span>Componentes</span>
        <span>Ambientes</span>
        <span>Infraestrutura</span>
        <span>Endereço</span>
      </div>
      <div className="relative min-w-[76rem] pb-2">
        <svg
          className="pointer-events-none absolute left-0 top-0 z-0"
          width={canvas.width}
          height={canvas.height}
          aria-hidden="true"
        >
          {paths.map((path) => (
            <path
              key={path.id}
              d={path.d}
              fill="none"
              stroke={path.color}
              strokeWidth={2}
              strokeLinecap="round"
            />
          ))}
        </svg>

        <div ref={contentRef} className="relative z-10 flex items-center gap-16">
          <article
            ref={bindNode('product')}
            className="flex w-48 shrink-0 items-start gap-3 rounded-2xl border border-violet-100 bg-white p-3 shadow-sm"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-violet-100 text-violet-600">
              <DocumentTextIcon className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-secondary-900">{composition.name}</span>
              <span className="mt-1 block text-xs text-secondary-500">
                {applications.length === 1
                  ? '1 componente'
                  : `${applications.length} componentes`}
              </span>
            </span>
          </article>

          <div className="flex flex-col gap-6">
            {applications.map((application, index) => {
              const color = FLOW_COLORS[index % FLOW_COLORS.length];
              const Icon = COMPONENT_ICONS[index % COMPONENT_ICONS.length];
              const hostings = environmentKind
                ? application.hostings.filter((hosting) => hosting.environmentKind === environmentKind)
                : application.hostings;
              const environments = groupEnvironments(hostings);
              return (
                <div key={application.id} className="flex items-center gap-16">
                  <button
                    type="button"
                    ref={bindNode(`app:${application.id}`)}
                    onClick={() => onSelectApplication?.(application.id)}
                    aria-pressed={selectedApplicationId === application.id}
                    aria-label={`Ver detalhes de ${application.name}`}
                    className={`relative flex w-48 shrink-0 items-center gap-3 rounded-2xl border bg-white px-3 py-2.5 text-left shadow-sm transition-colors ${
                      selectedApplicationId === application.id
                        ? 'border-sky-500 ring-2 ring-sky-100'
                        : 'border-secondary-200 hover:border-sky-300'
                    }`}
                  >
                    <span
                      className="absolute right-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 translate-x-1/2 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-secondary-50"
                      style={{ color }}
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-secondary-900">
                        {application.name}
                      </span>
                      <span className="block text-xs text-secondary-500">
                        {application.version || '—'}
                      </span>
                    </span>
                  </button>

                  <div className="flex flex-col gap-3">
                    {environments.length === 0 ? (
                      <span className="text-sm text-secondary-400">
                        {environmentKind ? 'Sem implantação neste ambiente' : 'Sem ambiente'}
                      </span>
                    ) : (
                      environments.map((environment) => (
                        <div key={environment.environmentKind} className="flex items-center gap-16">
                          <span
                            ref={bindNode(`env:${application.id}:${environment.environmentKind}`)}
                            className="relative inline-flex w-40 shrink-0 items-center gap-2 rounded-full border border-secondary-200 bg-white px-3 py-1.5 text-sm text-secondary-800 shadow-sm"
                          >
                            <span
                              className="absolute left-0 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                            <span
                              className={`h-2 w-2 rounded-full ${ENVIRONMENT_DOT[environment.environmentKind] ?? 'bg-secondary-400'}`}
                            />
                            {environmentLabel(environment.environmentKind)}
                            <span
                              className="absolute right-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 translate-x-1/2 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                          </span>
                          <div className="flex flex-col gap-2">
                            {environment.servers.map((server) => {
                              const address = server.address?.trim() || '—';
                              return (
                                <div key={server.id} className="flex items-center gap-16">
                                  <article
                                    ref={bindNode(
                                      `server:${application.id}:${environment.environmentKind}:${server.id}`,
                                    )}
                                    className="relative flex w-48 shrink-0 items-center gap-2 rounded-xl border border-secondary-200 bg-white px-3 py-2 shadow-sm"
                                  >
                                    <span
                                      className="absolute left-0 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                                      style={{ backgroundColor: color }}
                                    />
                                    <ServerStackIcon className="h-4 w-4 shrink-0 text-secondary-400" aria-hidden="true" />
                                    <span className="min-w-0">
                                      <span className="block truncate text-sm font-medium text-secondary-900">
                                        {server.infrastructureAssetName}
                                      </span>
                                      {server.infrastructureVendor ? (
                                        <span className="block truncate text-xs text-secondary-500">
                                          {server.infrastructureVendor}
                                        </span>
                                      ) : null}
                                    </span>
                                    <span
                                      className="absolute right-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 translate-x-1/2 rounded-full"
                                      style={{ backgroundColor: color }}
                                    />
                                  </article>
                                  <span
                                    ref={bindNode(
                                      `address:${application.id}:${environment.environmentKind}:${server.id}`,
                                    )}
                                    title={address === '—' ? undefined : address}
                                    className="relative inline-flex w-56 shrink-0 items-center rounded-xl border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-800 shadow-sm"
                                  >
                                    <span
                                      className="absolute left-0 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                                      style={{ backgroundColor: color }}
                                    />
                                    <span className="block min-w-0 truncate">{address}</span>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
